import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { debounceTime, filter, fromEvent, merge, startWith, switchMap, takeUntil } from 'rxjs'
import { fromResizeObserver } from '@schmancy/directives/layout'

export interface SchmancyScrollEvent extends CustomEvent<{
	scrollTop: number
	scrollHeight: number
	clientHeight: number
	e: Event
	scrollLeft?: number
	scrollWidth?: number
	clientWidth?: number
}> {}

export interface SchmancyScrollCommandEvent extends CustomEvent<{
	name: string
	action: 'scrollTo'
	top: number
	left?: number
}> {}

declare global {
	interface HTMLElementEventMap {
		scroll: SchmancyScrollEvent
		'schmancy-scroll-command': SchmancyScrollCommandEvent
	}
}

/**
 * How far in from an edge the content thins out, per axis — the two differ
 * because the unit the fade has to visibly cut differs. Along the inline axis
 * that unit is a chip or a card whose content runs continuously to the edge,
 * and 32px already washes it. Along the block axis it is a ROW: measured on
 * the nav rail's own stacked anatomy, a 32px fade reaches only the row's
 * caption and leaves its icon at full ink, so the caption reads as dimmed
 * while the row still reads as the last one. 64px reaches past the icon and
 * the whole row reads as thinning out, which is the claim the fade is making.
 *
 * Each default is measured against ONE unit, so a consumer whose scroll unit
 * differs states its own ramp by inheriting `--schmancy-scroll-fade-inline` or
 * `--schmancy-scroll-fade-block` onto the scroller; 2rem and 4rem stay the
 * values for every consumer that says nothing. A scroller of PROSE is the
 * block case: 4rem was measured for a row, and against a 20px line box it
 * takes the last line to nothing and washes two more, so a surface read
 * line-for-line loses the lines the fade is claiming continue. Neither ramp
 * can be a class on the host, because updateFadeState() writes the per-edge
 * length inline and an inline declaration outranks one.
 *
 * A ramp signals only as far as the ground behind the content differs from it
 * in tone, so no length works where the two sit within a few percent of each
 * other — a band of cards on a surface a shade off their own: the mask has
 * almost no tone to remove from the card and every bit of it to remove from
 * the figures printed on it, so it erases the data while the cut stays exactly
 * where it was. That consumer leaves `fade` off and wraps the scroller in
 * `<schmancy-scroll-rail>`, which paints the edge into shade instead of
 * thinning what sits on it.
 */
const EDGE_FADE_INLINE = 'var(--_fade-inline)'
const EDGE_FADE_BLOCK = 'var(--_fade-block)'

/**
 * Chaining is contained on the axis this scroller actually scrolls and left
 * alone on the other. An overflow-hidden axis is still a scroll container with
 * zero range, so containing THAT one swallows every gesture on it instead of
 * letting it through — a vertical drag anywhere on a horizontal rail would stop
 * the page dead. A container embedding this scroller inside a longer scrolling
 * page relaxes chaining across its whole subtree by inheriting
 * `--schmancy-overscroll-behavior` onto it; the property inherits through shadow
 * roots and slots, so it reaches nested scrollers this component never sees.
 * Longhands, not the shorthand — a var() in a shorthand makes both longhands
 * pending-substitution values, which round-trips badly through CSSOM.
 */
const OVERSCROLL_CONTAIN = 'var(--schmancy-overscroll-behavior, contain)'

/**
 * Scrollable container with a hideable scrollbar, direction control, and debounced scroll events.
 *
 * @fires {SchmancyScrollEvent} scroll - Fired when scrolling occurs (with a configurable debounce)
 * @csspart scroller - The host element (also exposed via the `.scroller` property)
 *
 * @example
 * ```html
 * <schmancy-scroll hide name="main-content">
 *   <div>Scrollable content goes here</div>
 * </schmancy-scroll>
 * ```
 *
 * @example
 * ```html
 * <schmancy-scroll direction="horizontal" hide name="image-carousel">
 *   <div class="flex">
 *     <img src="image1.jpg" alt="Image 1">
 *     <img src="image2.jpg" alt="Image 2">
 *   </div>
 * </schmancy-scroll>
 * ```
 *
 * @example Programmatic scroll with Lit ref
 * ```typescript
 * private scrollRef = createRef<HTMLElement>()
 *
 * // In template:
 * html`<schmancy-scroll ${ref(this.scrollRef)}>...</schmancy-scroll>`
 *
 * // Scroll to top (smooth — host has scroll-behavior: smooth):
 * this.scrollRef.value?.scrollTo({ top: 0 })
 * ```
 */
@customElement('schmancy-scroll')
export class SchmancyScroll extends SchmancyElement {
	static styles = [
		css`
			:host {
				width: 100%;
				min-height: 0;
				flex: 1;
				box-sizing: border-box;
				display: block;
				position: relative;
				scroll-behavior: smooth;
			}
			/* Fallback for non-flex contexts */
			:host(.explicit-height) {
				height: 100%;
				flex: none;
			}
			:host([hide]) {
				-ms-overflow-style: none; /* IE and Edge */
				scrollbar-width: none; /* Firefox */
			}
			:host([hide])::-webkit-scrollbar {
				display: none; /* Chrome, Safari, and Opera */
			}

			/*
			 * The edge fade thins the CONTENT out, rather than painting a gradient
			 * over it, because a painted gradient has to name the colour of the
			 * ground behind the scroller and this component cannot know it: the
			 * same scroller sits on containerLowest inside a nav rail, on surface
			 * on a page, and on whatever tier a consumer's own surface paints.
			 * A mask lets that real ground through exactly, in either scheme, with
			 * no token to keep in sync with the consumer's.
			 *
			 * The colour stops are alpha, not paint: mask-mode resolves to alpha for
			 * a gradient, so black means keep and transparent means drop, and
			 * neither is a colour the theme has any say over. Each edge's
			 * length is a custom property updateFadeState() writes as it measures
			 * which edges still have content past them, and 0px collapses that
			 * edge's gradient to a hard stop at the origin — fully opaque, the edge
			 * off. mask-composite intersects the four so they act independently.
			 * The 25% ceiling is the floor case: in a scroller only a couple of
			 * rows tall the fade would otherwise erase most of what is on screen
			 * in order to say there is more of it.
			 */
			:host([fade]) {
				--_fade-block: var(--schmancy-scroll-fade-block, 4rem);
				--_fade-inline: var(--schmancy-scroll-fade-inline, 2rem);
				--schmancy-scroll-fade-top: 0px;
				--schmancy-scroll-fade-bottom: 0px;
				--schmancy-scroll-fade-left: 0px;
				--schmancy-scroll-fade-right: 0px;
				-webkit-mask-image: linear-gradient(to bottom, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-top), 25%)),
					linear-gradient(to top, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-bottom), 25%)),
					linear-gradient(to right, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-left), 25%)),
					linear-gradient(to left, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-right), 25%));
				mask-image: linear-gradient(to bottom, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-top), 25%)),
					linear-gradient(to top, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-bottom), 25%)),
					linear-gradient(to right, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-left), 25%)),
					linear-gradient(to left, transparent 0, black clamp(0px, var(--schmancy-scroll-fade-right), 25%));
				-webkit-mask-repeat: no-repeat;
				mask-repeat: no-repeat;
				-webkit-mask-size: 100% 100%;
				mask-size: 100% 100%;
				-webkit-mask-composite: source-in;
				mask-composite: intersect;

				/*
				 * A ramp is fully transparent AT the edge, and the browser parks
				 * whatever it scrolls into view — a caret, a focused control, a
				 * scrollIntoView() target — flush against that same edge. So a faded
				 * scrollport that reserves nothing lands its own caret at zero alpha,
				 * on every keystroke, and the surface being typed into is the one that
				 * disappears. Scroll-padding equal to the ramp moves that landing to
				 * where the ramp ends, in full ink. It reads the same property the mask
				 * does, so retuning a ramp retunes its padding and the two cannot
				 * drift, and it takes the mask's own 25% ceiling so a short scroller
				 * cannot reserve the whole of itself.
				 */
				scroll-padding-block-start: min(var(--_fade-block), 25%);
				scroll-padding-block-end: min(var(--_fade-block), 25%);
				scroll-padding-inline-start: min(var(--_fade-inline), 25%);
				scroll-padding-inline-end: min(var(--_fade-inline), 25%);
			}
		`,
	]

	@property({ type: Boolean, reflect: true })
	public hide = false

	/** Targets this instance for the global `@schmancy:scrollTo` command event. */
	@property({ type: String, reflect: true })
	public name?: string

	@property({ type: String, reflect: true })
	public direction: 'vertical' | 'horizontal' | 'both' = 'both'

	/**
	 * Opt-in edge fade — the content thins out on whichever edges currently
	 * have more to scroll to, so a rail whose scrollbar is hidden (a chip row,
	 * a carousel, a nav-rail column) still says there is more. Ground-agnostic
	 * by construction: see the `:host([fade])` block above for why it masks
	 * rather than paints. Off by default — no visual change for existing
	 * consumers.
	 */
	@property({ type: Boolean, reflect: true })
	public fade = false

	/**
	 * What this region holds — "the line items of RE231247", "the navigation".
	 * A scroller that actually scrolls becomes a keyboard stop (see
	 * `updateFadeState`), and a stop with no name announces as a bare "region".
	 * Unset on a scroller that never overflows costs nothing; unset on one that
	 * does warns in DEV.
	 */
	@property({ type: String })
	public label?: string

	@query('slot', true)
	private slotEl!: HTMLSlotElement

	/** One DEV warning per instance, not one per resize. */
	#warnedUnnamed = false

	/** The host element itself — schmancy-scroll has no inner scroll wrapper. */
	get scroller(): HTMLElement {
		return this
	}

	/** Milliseconds. */
	@property({ type: Number })
	public debounce = 10

	/**
	 * @param options - ScrollToOptions, or (legacy) the top position as a number
	 * @param top - Legacy only: truthy selects 'smooth' behavior when `options` is a number
	 */
	public override scrollTo(options?: ScrollToOptions | number, top?: number): void {
		if (typeof options === 'number') {
			super.scrollTo({ top: options, behavior: top ? 'smooth' : 'auto' })
		} else if (options) {
			super.scrollTo(options)
		} else {
			super.scrollTo({ top: 0, left: 0, behavior: 'auto' })
		}
	}

	public scrollToLeft(left: number, behavior: ScrollBehavior = 'auto'): void {
		super.scrollTo({ left, behavior })
	}

	connectedCallback(): void {
		super.connectedCallback()
		this.updateScrollingStyles()
		this.updateLayoutContext()
		// Joins whatever the consumer named this scroller, rather than replacing
		// it: `<schmancy-scroll part="nav">` is a mount saying "this scrollport
		// IS my nav region", and overwriting the attribute took that name away
		// the moment the element connected. Read/add/write on the attribute
		// rather than `this.part.add` — `Element.part` (a DOMTokenList) is
		// unimplemented in happy-dom, so a direct call throws there; going
		// through the attribute serves both environments identically and still
		// dedupes so a reconnect (a cached template, an overlay that reparents)
		// never accumulates a second `scroller`.
		const existingParts = (this.getAttribute('part') ?? '').split(/\s+/).filter(Boolean)
		if (!existingParts.includes('scroller')) {
			this.setAttribute('part', [...existingParts, 'scroller'].join(' '))
		}
	}

	private updateScrollingStyles(): void {
		if (this.direction === 'horizontal') {
			this.style.setProperty('overflow-y', 'hidden')
			this.style.setProperty('overflow-x', 'auto')
		} else if (this.direction === 'vertical') {
			this.style.setProperty('overflow-y', 'auto')
			this.style.setProperty('overflow-x', 'hidden')
		} else {
			this.style.setProperty('overflow-y', 'auto')
			this.style.setProperty('overflow-x', 'auto')
		}
		this.style.setProperty('overscroll-behavior-x', this.direction === 'vertical' ? 'auto' : OVERSCROLL_CONTAIN)
		this.style.setProperty('overscroll-behavior-y', this.direction === 'horizontal' ? 'auto' : OVERSCROLL_CONTAIN)
	}

	private updateLayoutContext(): void {
		// rAF so the parent's layout has settled before reading computed styles
		requestAnimationFrame(() => {
			const parent = this.parentElement
			if (parent) {
				const parentStyles = getComputedStyle(parent)
				const isFlexParent = parentStyles.display === 'flex' || parentStyles.display === 'inline-flex'

				if (isFlexParent) {
					this.classList.remove('explicit-height')
				} else {
					this.classList.add('explicit-height')
				}
			} else {
				this.classList.add('explicit-height')
			}
		})
	}

	protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
		super.updated(changedProperties)
		if (changedProperties.has('direction')) {
			this.updateScrollingStyles()
		}
		if (changedProperties.has('label')) {
			if (!this.label && this.getAttribute('aria-label') === changedProperties.get('label'))
				this.removeAttribute('aria-label')
			this.updateFadeState()
		}
		// Runs on every update — there's no changedProperties signal for "parent's layout changed".
		this.updateLayoutContext()
	}

	protected firstUpdated(): void {
		// The host raises its own `scroll` below, and an inner scroller's bubbles here; only a real scroll is news.
		fromEvent(this.scroller, 'scroll', {
			passive: true,
		})
			.pipe(
				filter(event => !(event instanceof CustomEvent)),
				debounceTime(this.debounce),
				takeUntil(this.disconnecting),
			)
			.subscribe(e => {
				// scrollTop/scrollHeight/clientHeight/e are the original, required shape;
				// the scroll*/client* horizontal fields were added later as optional.
				const scrollTop = this.scroller.scrollTop
				const scrollHeight = this.scroller.scrollHeight
				const clientHeight = this.scroller.clientHeight

				const scrollLeft = this.scroller.scrollLeft
				const scrollWidth = this.scroller.scrollWidth
				const clientWidth = this.scroller.clientWidth

				this.dispatchEvent(
					new CustomEvent('scroll', {
						detail: {
							scrollTop,
							scrollHeight,
							clientHeight,
							e,
							scrollLeft,
							scrollWidth,
							clientWidth,
						},
						bubbles: true,
						composed: true,
					}) as SchmancyScrollEvent,
				)

				this.updateFadeState()
			})

		fromEvent<SchmancyScrollCommandEvent>(window, '@schmancy:scrollTo')
			.pipe(
				filter(e => this.name !== undefined && e.detail.name === this.name),
				takeUntil(this.disconnecting),
			)
			.subscribe(e => {
				if (e.detail.action === 'scrollTo' && typeof e.detail.top === 'number') {
					const options: ScrollToOptions = {
						behavior: 'smooth',
						top: e.detail.top,
					}

					if (typeof e.detail.left === 'number') {
						options.left = e.detail.left
					}

					this.scrollTo(options)
				}
			})

		// Content — not just container — geometry drives the fade, and the two
		// need separate observers: the host resizing is what the scroller's own
		// ResizeObserver sees, while content GROWING inside a fixed-height
		// scroller resizes nothing the scroller can observe about itself. The
		// slot's own `slotchange` doesn't cover it either — a consumer that
		// slots one wrapper and fills THAT (the nav rail slots its rows
		// container, then the app's own items arrive inside it once permissions
		// resolve) changes the wrapper's height without ever re-assigning the
		// slot. So each assigned element is observed too, re-subscribed
		// whenever the assignment itself changes.
		//
		// Subscribed regardless of `fade`, because gating the SUBSCRIPTION on a
		// property read once at first update strands any consumer that turns
		// the fade on later; `updateFadeState` is the gate instead. One
		// ResizeObserver per scroller plus one per assigned element, each
		// firing only on a real size change and coalesced at 16ms — a cost of
		// the same order as the scroll listener already mounted above.
		merge(
			fromResizeObserver(this.scroller),
			fromEvent(this.slotEl, 'slotchange').pipe(
				startWith(null),
				switchMap(() => merge(...this.slotEl.assignedElements().map(el => fromResizeObserver(el)))),
			),
		)
			.pipe(startWith(null), debounceTime(16), takeUntil(this.disconnecting))
			.subscribe(() => this.updateFadeState())
	}

	/**
	 * Recomputes which scroll edges currently have more content past them and
	 * writes each one's mask length onto the host. A style write rather than a
	 * render: the whole consumption of these four booleans is one custom
	 * property each, and the mask that reads them is static CSS.
	 */
	private updateFadeState(): void {
		const el = this.scroller
		const canScrollX = el.scrollWidth > el.clientWidth + 1
		const canScrollY = el.scrollHeight > el.clientHeight + 1

		// KEYBOARD REACH, measured rather than declared. A scroll container that
		// actually has somewhere to scroll is operable by arrow keys only once it
		// can hold focus (WCAG 2.1.1). Chromium 127+ focuses an overflowing
		// scroller on its own, which is a browser mitigation and not a contract:
		// measured in WebKit, 90 tabs never reached this host, so a horizontally
		// scrolled table's last columns were unreachable outright in Safari.
		//
		// Conditional on overflow, because a scroller with nothing past its edge
		// is not a region anybody needs to stop on, and every consumer that never
		// overflows keeps the tab order it has today.
		const scrolls = (this.direction !== 'vertical' && canScrollX) || (this.direction !== 'horizontal' && canScrollY)
		if (scrolls) {
			if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0')
			this.setAttribute('role', 'region')
			if (this.label) this.setAttribute('aria-label', this.label)
			if (import.meta.env.DEV && !this.label && !this.#warnedUnnamed) {
				this.#warnedUnnamed = true
				console.warn(
					'[schmancy] <schmancy-scroll> has content past its edge, so it is a keyboard stop, but it has no `label` — it announces as a bare "region". Give it the noun it holds: label="the line items of RE231247".',
					this,
				)
			}
		} else {
			this.removeAttribute('tabindex')
			this.removeAttribute('role')
			this.removeAttribute('aria-label')
		}

		if (!this.fade) return
		const inline = (hasMore: boolean) => (hasMore ? EDGE_FADE_INLINE : '0px')
		const block = (hasMore: boolean) => (hasMore ? EDGE_FADE_BLOCK : '0px')

		this.style.setProperty(
			'--schmancy-scroll-fade-left',
			inline(this.direction !== 'vertical' && canScrollX && el.scrollLeft > 1),
		)
		this.style.setProperty(
			'--schmancy-scroll-fade-right',
			inline(this.direction !== 'vertical' && canScrollX && el.scrollLeft < el.scrollWidth - el.clientWidth - 1),
		)
		this.style.setProperty(
			'--schmancy-scroll-fade-top',
			block(this.direction !== 'horizontal' && canScrollY && el.scrollTop > 1),
		)
		this.style.setProperty(
			'--schmancy-scroll-fade-bottom',
			block(this.direction !== 'horizontal' && canScrollY && el.scrollTop < el.scrollHeight - el.clientHeight - 1),
		)
	}

	protected render() {
		// All styling lives on :host — no inner wrapper element, and the edge
		// fade is the host's own mask rather than an overlay to render.
		return html`<slot></slot>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-scroll': SchmancyScroll
	}
}
